/**
 * First-Session-Guidance (client#240) — floating "Erste Schritte" checklist
 * for a player's first running session.
 *
 * Non-blocking panel (bottom-right, above content, never a modal): new beta
 * testers see the four first-day steps and each one checks off from REAL
 * game state on every state_update — no separate tutorial mode, no fake
 * progress. Once every step has been observed done (or the player dismisses
 * the panel), the dismissal is persisted via PATCH
 * /api/players/me/preferences (onboardingDismissed) so it follows the
 * account across devices — explicitly NOT localStorage-only per issue #240.
 *
 * Step completion is monotonic within the page session: playerReady resets
 * server-side after each tick, so a step that was seen done stays checked
 * (seenDone set) instead of flickering back to open.
 */
import { fixture } from '../data/fixture';
import { patchPreferences } from '../api';
import { t } from '../i18n';

type StepKey = 'plan' | 'team' | 'ready' | 'tick';

interface StepDef {
  key: StepKey;
  done(): boolean;
}

const STEPS: StepDef[] = [
  // Queue at least one action for the day.
  { key: 'plan', done: () => ((fixture as any)?.player?.plan?.length ?? 0) > 0 },
  // Form a team (optional mechanically, but the core mid-game lever).
  { key: 'team', done: () => ((fixture as any)?.player?.teams?.length ?? 0) > 0 },
  // Mark READY so the tick can resolve.
  { key: 'ready', done: () => !!(fixture as any)?.playerReady },
  // Survive the first tick (day advances past the session start).
  { key: 'tick', done: () => ((fixture as any)?.day ?? 0) > 0 },
];

// null = preferences not loaded yet → render nothing until we know.
let dismissed: boolean | null = null;
const seenDone = new Set<StepKey>();
let persisted = false;

/** Called from boot() once GET /api/players/me/preferences resolves. */
export function setOnboardingDismissed(v: boolean): void {
  dismissed = v;
  refreshOnboarding();
}

/** Test-only: reset module state between cases. */
export function resetOnboardingForTest(): void {
  dismissed = null;
  persisted = false;
  seenDone.clear();
  document.getElementById('onboarding-panel')?.remove();
}

function persistDismissed(): void {
  if (persisted) return;
  persisted = true;
  dismissed = true;
  patchPreferences({ onboardingDismissed: true }).catch(() => {
    // Offline/api hiccup — panel stays gone for this page session anyway;
    // worst case the checklist reappears on next load.
    persisted = false;
  });
}

function ensureStyles(): void {
  if (document.getElementById('onboarding-styles')) return;
  const style = document.createElement('style');
  style.id = 'onboarding-styles';
  style.textContent = `
    #onboarding-panel {
      position: fixed; right: 16px; bottom: 16px; z-index: 900;
      width: 280px; padding: 12px 14px;
      background: rgba(10, 14, 18, 0.94);
      border: 1px solid var(--line);
      box-shadow: 0 0 24px rgba(0, 0, 0, 0.5);
    }
    #onboarding-panel .ob-head {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 8px;
    }
    #onboarding-panel .ob-close {
      background: none; border: none; color: var(--text-dim, #7a8a99);
      cursor: pointer; font-size: 1rem; line-height: 1; padding: 2px 4px;
    }
    #onboarding-panel ul { list-style: none; margin: 0 0 10px; padding: 0; }
    #onboarding-panel li {
      display: flex; gap: 8px; align-items: baseline;
      padding: 3px 0; font-size: 0.82rem;
    }
    #onboarding-panel li.ob-done { color: var(--text-dim, #7a8a99); text-decoration: line-through; }
    #onboarding-panel li .ob-mark { color: var(--cyan); min-width: 1em; }
    #onboarding-panel .ob-all-done { font-size: 0.82rem; color: var(--cyan); margin-bottom: 10px; }
  `;
  document.head.appendChild(style);
}

/**
 * Re-evaluate the checklist against the live fixture and (re)render the
 * panel. Call on every state_update; cheap no-op once dismissed/complete.
 */
export function refreshOnboarding(): void {
  const existing = document.getElementById('onboarding-panel');

  // Not eligible: prefs unknown, dismissed, or no session state yet.
  if (dismissed !== false || !(fixture as any)?.player) {
    existing?.remove();
    return;
  }

  for (const s of STEPS) {
    if (s.done()) seenDone.add(s.key);
  }
  const allDone = seenDone.size === STEPS.length;
  if (allDone) persistDismissed();

  ensureStyles();
  const panel = existing ?? document.createElement('div');
  panel.id = 'onboarding-panel';
  panel.className = 'panel';
  panel.innerHTML = `
    <div class="ob-head">
      <span class="eyebrow">${t('onboarding.title')}</span>
      <button class="ob-close" id="onboarding-dismiss" title="${t('onboarding.dismiss')}" aria-label="${t('onboarding.dismiss')}">✕</button>
    </div>
    ${allDone
      ? `<div class="ob-all-done">${t('onboarding.done')}</div>`
      : `<ul>${STEPS.map(s => {
          const done = seenDone.has(s.key);
          return `<li class="${done ? 'ob-done' : ''}"><span class="ob-mark">${done ? '✓' : '○'}</span>${t('onboarding.step.' + s.key)}</li>`;
        }).join('')}</ul>`}
    <a class="btn sm" href="https://wiki.corpllm.io/how-to-play/" target="_blank" rel="noopener">${t('onboarding.help')}</a>
  `;
  panel.querySelector('#onboarding-dismiss')!.addEventListener('click', () => {
    persistDismissed();
    panel.remove();
  });
  if (!existing) document.body.appendChild(panel);

  // Completed: leave the "all done" note visible briefly, then fade out.
  if (allDone) {
    setTimeout(() => panel.remove(), 8000);
  }
}
